import { z } from "zod";

export const PROVISION_PRESET_VALUES = ["30", "90", "custom"] as const;
export const NOTICE_PERIOD_PRESET_VALUES = ["30", "60", "90", "custom"] as const;
export type ProvisionPreset = (typeof PROVISION_PRESET_VALUES)[number];
export type NoticePeriodPreset = (typeof NOTICE_PERIOD_PRESET_VALUES)[number];

export const hrApproveFormSchema = z
  .object({
    departmentId: z.string().min(1, "Department is required"),
    designationId: z.string().min(1, "Designation is required"),
    managerId: z.string().min(1, "Manager is required"),
    companyName: z.string().min(1, "Company name is required"),
    employeeType: z.string().min(1, "Employee type is required"),
    leaveManagerId: z.string().min(1, "Leave manager is required"),
    employeeSubType: z.string().optional(),
    isTech: z.boolean(),
    noticePeriodPreset: z.enum(NOTICE_PERIOD_PRESET_VALUES),
    customNoticePeriod: z
      .object({
        amount: z.number().min(1, "Custom notice period must be at least 1"),
        unit: z.enum(["day", "month"]),
      })
      .optional(),
    provisionPreset: z.enum(PROVISION_PRESET_VALUES),
    customProvision: z
      .object({
        amount: z.number().min(1, "Custom provision period must be at least 1"),
        unit: z.enum(["day", "month"]),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.employeeType === "REGULAR" && !data.employeeSubType?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Employee sub type is required",
        path: ["employeeSubType"],
      });
    }
    if (data.provisionPreset === "custom" && data.customProvision == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Custom provision period is required",
        path: ["customProvision"],
      });
    }
    if (data.noticePeriodPreset === "custom" && data.customNoticePeriod == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Custom notice period is required",
        path: ["customNoticePeriod"],
      });
    }
  });

export type HrApproveFormValues = z.infer<typeof hrApproveFormSchema>;

/** Query parameters sent to `PUT /onboarding/v2/{id}/approve`. */
export type HrApproveQueryParams = {
  companyName: string;
  employeeType: string;
  managerId: string;
  designationId: string;
  leaveManagerId: string;
  employeeSubType: string;
  isTech: boolean;
  confirmationDate: string;
  noticePeriodDays: number;
};

export function buildHrApproveQueryParams(
  values: HrApproveFormValues,
  confirmationDate: string,
  noticePeriodDays: number,
): HrApproveQueryParams {
  const params: HrApproveQueryParams = {
    employeeType: values.employeeType,
    companyName: values.companyName,
    managerId: values.managerId,
    designationId: values.designationId,
    leaveManagerId: values.leaveManagerId,
    employeeSubType: values.employeeType === "REGULAR" ? values.employeeSubType?.trim() ?? "" : "",
    isTech: values.isTech,
    confirmationDate,
    noticePeriodDays,
  };
  return params;
}

export function hrApproveQueryParamsToSearch(params: HrApproveQueryParams): string {
  const q = new URLSearchParams({
    companyName: params.companyName,
    employeeType: params.employeeType,
    managerId: params.managerId,
    designationId: params.designationId,
    leaveManagerId: params.leaveManagerId,
    employeeSubType: params.employeeSubType,
    isTech: String(params.isTech),
    confirmationDate: params.confirmationDate,
    noticePeriodDays: String(params.noticePeriodDays),
  });
  return q.toString();
}
